import type { AttendanceTrend, HomeFigures, NoShowRisk, SlotOffer } from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The two headline figures that need more than the day's appointments.
 *
 * **Branch-scoped**, because both are about today in one location: chairs and
 * the waitlist belong to a branch, and "at the desk on exit" is a desk.
 *
 * The date is the branch's own local date, passed explicitly — never derived
 * from the server's clock, which is in whatever timezone the host happens to be.
 */
export function readHomeFigures(date: string, signal?: AbortSignal): Promise<HomeFigures> {
  return api<HomeFigures>(`/home/figures?date=${date}`, { signal, branchScoped: true });
}

/**
 * The times today that would fit one waiting patient.
 *
 * Found by the same rule the conflict service uses, so an offer the desk takes
 * is one the server will accept — the property STORY-035's acceptance check
 * demands of the slot picker, and the reason this does not compute its own.
 */
export function readSlotOffers(
  entryId: string,
  date: string,
  signal?: AbortSignal,
): Promise<SlotOffer[]> {
  return api<SlotOffer[]>(`/home/waitlist/${entryId}/offers?date=${date}`, {
    signal,
    branchScoped: true,
  });
}

/** Today's patients who have been missing appointments (STORY-088, K4). */
export function readNoShowRisk(date: string, signal?: AbortSignal): Promise<NoShowRisk[]> {
  return api<NoShowRisk[]>(`/home/no-show-risk?date=${date}`, { signal, branchScoped: true });
}

/**
 * How reliably this branch's patients have been turning up (Anthony's QA, #17).
 *
 * A weekly series for the chart, the rate over the whole window, and the ten
 * patients who missed most. **Branch-scoped**, unlike the risk flags: those
 * read clinic-wide because a patient who misses here and attends there is still
 * worth confirming with, while this is one location's own performance.
 */
export function readAttendance(
  date: string,
  signal?: AbortSignal,
  weeks?: number,
): Promise<AttendanceTrend> {
  const period = weeks === undefined ? "" : `&weeks=${String(weeks)}`;
  return api<AttendanceTrend>(`/home/attendance?date=${date}${period}`, {
    signal,
    branchScoped: true,
  });
}
